import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Container, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  Select, Button, Flex, Text, Spinner, Card, CardBody, useToast,
  Stat, StatLabel, StatNumber, StatGroup, IconButton, VStack,
} from '@chakra-ui/react';
import { FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from 'recharts';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const STATUS_COLORS = {
  active: 'green',
  grace_period: 'orange',
  expired: 'red',
  canceled: 'gray',
};

const STATUS_LABELS = {
  active: 'Ativa',
  grace_period: 'Período de Carência',
  expired: 'Expirada',
};

const PLAN_LABELS = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  annual: 'Anual',
};

const PIE_COLORS = ['#38A169', '#ED8936', '#E53E3E', '#A0AEC0'];

const SubscriptionList = () => {
  const toast = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ active: 0, grace: 0, expired: 0, canceled: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const params = new URLSearchParams({ page, limit: 15 });
      if (statusFilter) params.set('status', statusFilter);

      const res = await fetch(`${API_BASE_URL}api/subscription/admin/list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao buscar assinaturas');
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
      setPagination(data.pagination || { page: 1, total: 0, totalPages: 1 });
      setSummary(data.summary || { active: 0, grace: 0, expired: 0, canceled: 0 });
    } catch (err) {
      toast({ title: 'Erro', description: err.message, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, toast]);

  useEffect(() => { fetchSubscriptions(); }, [fetchSubscriptions]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const pieData = [
    { name: 'Ativas', value: summary.active },
    { name: 'Carência', value: summary.grace },
    { name: 'Expiradas', value: summary.expired },
    { name: 'Canceladas', value: summary.canceled },
  ].filter(d => d.value > 0);

  const getStatusDisplay = (sub) => {
    if (sub.status === 'active' && sub.autoRenewOffAt) {
      return { label: 'Cancelada', color: 'gray' };
    }
    return { label: STATUS_LABELS[sub.status] || sub.status, color: STATUS_COLORS[sub.status] || 'gray' };
  };

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Assinaturas</Heading>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetchSubscriptions} isLoading={loading}>
          Atualizar
        </Button>
      </Flex>

      {/* Summary */}
      <Flex gap={6} mb={6} direction={{ base: 'column', lg: 'row' }}>
        <StatGroup flex={1}>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Ativas</StatLabel><StatNumber fontSize="lg" color="green.500">{summary.active}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Carência</StatLabel><StatNumber fontSize="lg" color="orange.500">{summary.grace}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1} mr={3}><CardBody py={3}>
            <Stat><StatLabel>Expiradas</StatLabel><StatNumber fontSize="lg" color="red.500">{summary.expired}</StatNumber></Stat>
          </CardBody></Card>
          <Card flex={1}><CardBody py={3}>
            <Stat><StatLabel>Canceladas</StatLabel><StatNumber fontSize="lg" color="gray.500">{summary.canceled}</StatNumber></Stat>
          </CardBody></Card>
        </StatGroup>

        {pieData.length > 0 && (
          <Card minW="280px">
            <CardBody py={2}>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}
      </Flex>

      {/* Filter */}
      <Card mb={4}>
        <CardBody py={3}>
          <HStack>
            <Select
              size="sm" maxW="200px" placeholder="Todos os status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="active">Ativa</option>
              <option value="grace_period">Período de Carência</option>
              <option value="expired">Expirada</option>
            </Select>
            {statusFilter && (
              <Button size="sm" variant="ghost" onClick={() => { setStatusFilter(''); setPage(1); }}>Limpar</Button>
            )}
          </HStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardBody p={0}>
          {loading ? (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          ) : subscriptions.length === 0 ? (
            <Flex justify="center" py={10}><Text color="gray.500">Nenhuma assinatura encontrada</Text></Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Profissional</Th>
                    <Th>Plano</Th>
                    <Th>Método</Th>
                    <Th>Vencimento</Th>
                    <Th>Auto-Renovar</Th>
                    <Th>Status</Th>
                    <Th>Desde</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {subscriptions.map((sub) => {
                    const statusDisplay = getStatusDisplay(sub);
                    return (
                      <Tr key={sub._id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">{sub.workerName}</Text>
                            <Text fontSize="xs" color="gray.500">{sub.workerEmail}</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge variant="subtle" colorScheme="teal" fontSize="xs">
                            {PLAN_LABELS[sub.planId] || sub.planId || '-'}
                          </Badge>
                        </Td>
                        <Td fontSize="sm">{sub.paymentMethod === 'credit_card' ? 'Cartão' : sub.paymentMethod === 'pix' ? 'PIX' : sub.paymentMethod || '-'}</Td>
                        <Td fontSize="sm">{formatDate(sub.currentPeriodEnd)}</Td>
                        <Td>
                          <Badge colorScheme={sub.autoRenew ? 'green' : 'gray'} fontSize="xs">
                            {sub.autoRenew ? 'Sim' : 'Não'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={statusDisplay.color} fontSize="xs">{statusDisplay.label}</Badge>
                        </Td>
                        <Td fontSize="sm">{formatDate(sub.createdAt)}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}

          {pagination.totalPages > 1 && (
            <Flex justify="space-between" align="center" p={3} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="sm" color="gray.500">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
              </Text>
              <HStack>
                <IconButton size="sm" icon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} />
                <IconButton size="sm" icon={<FiChevronRight />} isDisabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} />
              </HStack>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default SubscriptionList;
